import { ContentCopy } from '@mui/icons-material';
import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CopyToClipboard } from '../common/CopyToClipBoard';
import DetachablePanel from '../common/DetachablePanel';
import ToSearchLinks from '../common/ToSearchLinks';
import { CutterHistory } from '../types/cutterHistory';
import { navigateTabsWithQueries } from '../types/cutterState';

// ─── Panel wrapper ────────────────────────────────────────────────────────────

interface CutterDetailsHistoryInfoPanelProps {
    open: boolean;
    onClose: () => void;
    cutter: CutterHistory;
}

// ─── Shared style helpers ─────────────────────────────────────────────────────

const labelProps = {
    variant: 'caption' as const,
    component: 'span' as const,
    sx: { color: 'text.secondary', whiteSpace: 'nowrap' as const },
};

const searchLinkProps = {
    variant: 'caption' as const,
    sx: { color: 'text.primary', fontWeight: 500 },
};

interface SectionRow {
    label: string;
    value?: string | null;
    searchable?: boolean;
    copyable?: boolean;
    bold?: boolean;
}

interface Section {
    title: string;
    rows: SectionRow[];
}

// ─── Content (pure, reusable) ─────────────────────────────────────────────────
// Top counter keys per machine model (source: TBL_REPORT.Counters, ordered by occurrence)
const COUNTER_KEYS_BY_MODEL: Record<string, string[]> = {
    // Vector: ~147k rows each — time in h, distances in mm (ConveyorDistance) or m (CutPerimeter*)
    vector:   ['PowerOnTime', 'ConveyorDistance', 'MobileSetCutTime', 'CutPerimeter',
               'TimeCutting', 'CutPerimeterTotal', 'TimeWaiting', 'TimeIdle'],
    // Focus: ~2.7k rows — adds laser/waste counters
    focus:    ['PowerOnTime', 'CuttingWasteCounter', 'CutPerimeter', 'TimeCutting',
               'TimeWaiting', 'ConveyorDistance', 'MobileSetCutTime', 'CutPerimeterMachine'],
    // Versalis: ~7.4k rows — no EffectiveCuttingTime; lighting counters in h
    versalis: ['PowerOnTime', 'StripLight1On', 'StripLight2On', 'ConveyorDistance',
               'Light1On', 'Light2On', 'PulseTrigger'],
};
/** camelCase counter key → human-readable label  (e.g. 'PowerOnTime' → 'Power On Time') */
const counterLabel = (k: string): string => k.replace(/([A-Z]|\d+)/g, ' $1').trim();
interface CutterDetailsHistoryInfoProps {
    cutter: CutterHistory;
}

const CutterDetailsHistoryInfo: React.FC<CutterDetailsHistoryInfoProps> = ({ cutter }) => {
    const navigate = useNavigate();
    const st = cutter.state;
    const [activeTab, setActiveTab] = React.useState(0);

    // ── Latest counters (most recent report) ─────────────────────────────────
    const latestCounters = React.useMemo(() => {
        if (!cutter.reports?.length) return null;
        return ([...cutter.reports]
            .sort((a, b) => new Date(b.zipCreationDate).getTime() - new Date(a.zipCreationDate).getTime())[0]
            ?.counters) ?? null;
    }, [cutter.reports]);

    const fmtCounter = (key: string, v: number): string => {
        if (/[Tt]ime/.test(key) || /Light\d*On$/.test(key)) return `${v.toFixed(1)} h`;
        if (/kWh/i.test(key)) return `${Math.round(v).toLocaleString()} kWh`;
        return Math.round(v).toLocaleString();
    };

    const cv = (key: string): string | null => {
        if (!latestCounters) return null;
        const v = (latestCounters as Record<string, number | undefined>)[key];
        return v != null ? fmtCounter(key, v) : null;
    };

    const sections: Section[] = [
        {
            title: 'Cutter',
            rows: [
                /* { label: 'Product', value: st.harD_Product, searchable: true }, */
                /* { label: 'Original model', value: originalModel, searchable: true }, */
                { label: 'Product', value: st.harD_OriginalMachineType, searchable: true },
                /* { label: 'Machine type', value: st.harD_Machine_type, searchable: true }, */
                { label: 'Host ID', value: st.sofT_Main_Feature_HostId, copyable: true },
                {
                    label: 'Contract(s)',
                    value: st.hasContract === 1 ? st.lisA_Contract?.replace(/\|/g, ', ') : null,
                    searchable: true,
                },
                {
                    label: 'Delivery month',
                    value: st.harD_CUT_SoldDate
                        ? new Date(st.harD_CUT_SoldDate).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
                        : null,
                    copyable: true,
                },
                {
                    label: 'Last connection',
                    value: (() => {
                        const dates = [st.lisA_LastReport, st.dasH_LastEventDate]
                            .filter(Boolean)
                            .map(d => new Date(d).getTime())
                            .filter(t => !isNaN(t));
                        if (!dates.length) return null;
                        const maxDate = new Date(Math.max(...dates));
                        const dateStr = maxDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
                        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                        const tzShort = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
                            .formatToParts(maxDate).find(p => p.type === 'timeZoneName')?.value ?? tz;
                        return `${dateStr} (${tzShort}, ${tz})`;
                    })(),
                    copyable: true,
                },
            ],
        },
        {
            title: 'Counters',
            rows: (() => {
                const modelKey = (st.harD_Model ?? '').toLowerCase();
                const keys = COUNTER_KEYS_BY_MODEL[modelKey] ?? COUNTER_KEYS_BY_MODEL.vector;
                return [
                    // ── Contract-relevant (bold) ───────────────────────────────
                    { label: 'Effective cutting', value: st.cutR_EffectiveCuttingTime != null ? `${st.cutR_EffectiveCuttingTime.toFixed(1)} h` : null, bold: true },
                    { label: 'Vacuum',            value: st.cutR_VacuumTime != null ? `${st.cutR_VacuumTime.toFixed(1)} h` : null, bold: true },
                    // ── Model-specific counters (from latest report) ───────────────
                    ...keys.map(k => ({ label: counterLabel(k), value: cv(k) })),
                ];
            })(),
        },
        {
            title: 'Customer',
            rows: [
                { label: 'Company', value: st.cusT_CompanyName, searchable: true },
                { label: 'Plant', value: st.cusT_PlantName, searchable: true },
                { label: 'Plant ID', value: st.cusT_CompanyId, searchable: true },
                { label: 'Country', value: st.lisA_Country, searchable: true },
            ],
        }
    ];

    const copyText = [
        cutter.serialNumber,
        ...([] as string[]).concat(...sections.map((s: Section) => s.rows.filter((r: SectionRow) => r.value).map((r: SectionRow) => `${r.label}\t${r.value}`)))
    ].join('\n');

    const plantName = st.cusT_PlantName;
    const country = st.lisA_Country;
    const mapQuery = plantName && country ? `${plantName}, ${country}` : country || null;

    return (
        <Box sx={{ minWidth: 280, display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header: serial number + copy */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                <Typography variant="subtitle2" fontWeight="bold">{cutter.serialNumber}</Typography>
                <CopyToClipboard TextToCopy={copyText} AckMessage="Cutter info copied" title="Copy to clipboard"
                    sx={{ width: '1.4em', height: '1.4em', ml: 1 }}>
                    <ContentCopy sx={{ fontSize: '0.85rem' }} />
                </CopyToClipboard>
            </Stack>

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, v: number) => setActiveTab(v)}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 34, '& .MuiTab-root': { minHeight: 34, py: 0.25, fontSize: '0.72rem' } }}
            >
                <Tab label="Cutter" />
                <Tab label="Counters" />
                <Tab label="Customer" />
                <Tab label="Map" disabled={!mapQuery} />
            </Tabs>

            {/* Tab content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                {activeTab < 3 && (() => {
                    const visibleRows = sections[activeTab].rows.filter(r => r.value);
                    return visibleRows.length > 0 ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: '20px', rowGap: '2px', alignItems: 'center' }}>
                            {visibleRows.map(row => (
                                <React.Fragment key={row.label}>
                                    <Typography {...labelProps}>{row.label}:</Typography>
                                    <Box>
                                        {row.searchable
                                            ? <ToSearchLinks strInput={row.value!} {...searchLinkProps} />
                                            : row.copyable
                                                ? <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Typography variant="caption">{row.value}</Typography>
                                                    <CopyToClipboard TextToCopy={row.value!} AckMessage="Copied" title="Copy to clipboard"
                                                        sx={{ width: '1.2em', height: '1.2em' }}>
                                                        <ContentCopy sx={{ fontSize: '0.7rem' }} />
                                                    </CopyToClipboard>
                                                  </Stack>
                                                : <Typography variant="caption" fontWeight={row.bold ? 600 : undefined}>{row.value}</Typography>
                                        }
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="caption" color="text.secondary">No data available</Typography>
                    );
                })()}

                {activeTab === 1 && (
                    <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" variant="text"
                            onClick={() => navigateTabsWithQueries(navigate, 'bookmarks', cutter.serialNumber, 'system', 'counters')}
                            sx={{ fontSize: '0.7rem', p: '2px 4px', minWidth: 0 }}>
                            ↗️ View counters detail
                        </Button>
                    </Box>
                )}
                {activeTab === 3 && mapQuery && (
                    <Box
                        component="iframe"
                        src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`}
                        title={`Map of ${mapQuery}`}
                        sx={{ width: '100%', height: '340px', border: 0, borderRadius: 1, boxShadow: 1 }}
                    />
                )}
            </Box>
        </Box>
    );
};

// ─── DetachablePanel wrapper ──────────────────────────────────────────────────

const CutterDetailsHistoryInfoPanel: React.FC<CutterDetailsHistoryInfoPanelProps> = ({ open, onClose, cutter }) => {
    // Compute docked-right defaults (only when viewport is wide enough to show gutters)
    const { panelWidth: infoPanelWidth, panelHeight: infoPanelHeight, defaultPosition: infoDefaultPosition } = React.useMemo(() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const gutterW = Math.floor((vw - 936) / 2);
        if (gutterW < 150) return { panelWidth: undefined, panelHeight: undefined, defaultPosition: undefined };
        // 15px margin each side → width = gutterW - 30
        // x = (vw + 936) / 4  (positions right edge 15px from viewport right)
        // height = top-1/3 to bottom-10% = 17/30 of vh
        const panelHeight = Math.round(vh * 17 / 30);
        const dragY = Math.round(vh / 5 - (vh - panelHeight) / 2);
        return {
            panelWidth: gutterW - 30,
            panelHeight,
            defaultPosition: { x: Math.round((vw + 936) / 4), y: dragY },
        };
    }, []);

    return (
        <DetachablePanel
            open={open}
            onClose={onClose}
            panelId="cutter-info"
            title="Cutter info"
            subtitle={<Typography variant="subtitle2">{cutter.serialNumber}</Typography>}
            width={infoPanelWidth ?? { xs: '95vw', sm: '340px' }}
            height={infoPanelHeight}
            defaultPosition={infoDefaultPosition}
        >
            <CutterDetailsHistoryInfo cutter={cutter} />
        </DetachablePanel>
    );
};

export default CutterDetailsHistoryInfoPanel;
